import { useSheetContext } from '@/hooks/use-sheet-context'
import { useFormContext } from '@/lib/form-contexts'
import { useStore } from '@tanstack/react-form'
import { useBlocker, type ShouldBlockFn } from '@tanstack/react-router'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../ui/button'
import LoadingSpinner from '../ui/loading-spinner'
import { Typography } from '@/components/ui/typography'

type BlockerLocation = Parameters<ShouldBlockFn>[0]['next']

/** Lets a save bar allow some navigation, such as moving between steps of the same form. */
export type SaveBarShouldBlockNavigation = (args: {
  next: BlockerLocation
  isDefaultValue: boolean
  hasUnsavedChanges: boolean
}) => boolean

type SaveBarResetHandler = (event: MouseEvent<HTMLButtonElement>) => void

/** A fixed save bar that a `SaveBarGroup` shows inside its combined bar. */
interface SaveBarGroupMember {
  id: string
  isDirty: boolean
  isDefaultValue: boolean
  isResetPending: boolean
  isSavePending: boolean
  onReset: SaveBarResetHandler
  onSave: () => unknown
  resetButtonText: string
  shouldBlockNavigation: SaveBarShouldBlockNavigation | null
  submitButtonText: string
  unsavedChangesText: string
}

interface SaveBarGroupContextValue {
  register: (id: string) => () => void
  update: (id: string, member: SaveBarGroupMember) => void
}

export interface SaveBarProps {
  onReset?: SaveBarResetHandler | null
  mutation?: { isPending?: boolean } | null
  onSave?: (() => unknown) | null
  submitButtonText?: string
  resetButtonText?: string
  unsavedChangesText?: string
  renderInPortal?: boolean
  forceDirty?: boolean
  layout?: 'fixed' | 'inline'
  shouldBlockNavigation?: SaveBarShouldBlockNavigation | null
  isInteractionPending?: boolean
  onDirtyChange?: ((hasUnsavedChanges: boolean) => void) | null
}

const SaveBarGroupContext = createContext<SaveBarGroupContextValue | null>(null)

function flushActiveField() {
  if (typeof document === 'undefined') return

  const activeElement = document.activeElement

  if (activeElement instanceof HTMLElement && activeElement !== document.body) {
    activeElement.blur()
  }
}

function FixedSaveBarFrame({
  children,
  unsavedChangesText,
}: {
  children: ReactNode
  unsavedChangesText: string
}) {
  return (
    <div className="fixed top-0 left-0 z-50 flex min-h-18 w-full items-center border-b border-border/70 bg-card/95 py-2 pr-2 shadow-sm backdrop-blur-md md:pr-8 dark:bg-elevated/95 dark:shadow-none">
      <div className="min-w-2 max-w-3xs shrink-0 md:w-3xs" />
      <div className="inline-flex w-full max-w-4xl flex-wrap items-center justify-between gap-3">
        <Typography variant="emphasis">{unsavedChangesText}</Typography>
        {children}
      </div>
    </div>
  )
}

function useUnsavedChangesBlocker({
  disabled = false,
  hasUnsavedChanges,
  shouldBlockNavigation,
}: {
  disabled?: boolean
  hasUnsavedChanges: boolean
  shouldBlockNavigation: ((args: { next: BlockerLocation }) => boolean) | null
}) {
  useBlocker({
    disabled,
    shouldBlockFn: ({ next }) => {
      if (!hasUnsavedChanges) return false
      // We shouldn't stop this because it's too late to save your work anyway if you're logged out
      if (next.fullPath === '/login') return false
      if (typeof shouldBlockNavigation === 'function' && !shouldBlockNavigation({ next })) {
        return false
      }

      const shouldLeave = confirm('You have unsaved changes. Are you sure you want to leave?')
      return !shouldLeave
    },
    enableBeforeUnload: !disabled && hasUnsavedChanges,
  })
}

export function SaveBarGroup({
  children,
  renderInPortal = true,
  resetButtonText = 'Reset all',
}: {
  children: ReactNode
  renderInPortal?: boolean
  resetButtonText?: string
}) {
  const isInSheet = useSheetContext()
  const [members, setMembers] = useState(() => new Map<string, SaveBarGroupMember | null>())

  const register = useCallback((id: string) => {
    setMembers((current) => {
      if (current.has(id)) return current

      const next = new Map(current)
      next.set(id, null)
      return next
    })

    return () => {
      setMembers((current) => {
        if (!current.has(id)) return current

        const next = new Map(current)
        next.delete(id)
        return next
      })
    }
  }, [])

  const update = useCallback((id: string, member: SaveBarGroupMember) => {
    setMembers((current) => {
      if (current.get(id) === member) return current

      const next = new Map(current)
      next.set(id, member)
      return next
    })
  }, [])

  const contextValue = useMemo(() => ({ register, update }), [register, update])
  const dirtyMembers = Array.from(members.values()).filter((member): member is SaveBarGroupMember =>
    Boolean(member?.isDirty)
  )
  const [onlyDirtyMember] = dirtyMembers.length === 1 ? dirtyMembers : []
  const anySavePending = dirtyMembers.some((member) => member.isSavePending)
  const anyResetPending = dirtyMembers.some((member) => member.isResetPending)

  useUnsavedChangesBlocker({
    disabled: dirtyMembers.length === 0,
    hasUnsavedChanges: dirtyMembers.length > 0,
    shouldBlockNavigation: ({ next }) =>
      dirtyMembers.some(
        (member) =>
          typeof member.shouldBlockNavigation !== 'function' ||
          member.shouldBlockNavigation({
            next,
            isDefaultValue: member.isDefaultValue,
            hasUnsavedChanges: member.isDirty,
          })
      ),
  })

  const groupedSaveBar = dirtyMembers.length ? (
    <FixedSaveBarFrame
      unsavedChangesText={
        onlyDirtyMember
          ? onlyDirtyMember.unsavedChangesText
          : `${dirtyMembers.length} sections have unsaved changes`
      }
    >
      <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
        <Button
          type="button"
          variant="link"
          onPointerDownCapture={flushActiveField}
          onClick={(event) => {
            flushActiveField()
            dirtyMembers.forEach((member) => member.onReset(event))
          }}
          disabled={anyResetPending}
        >
          {onlyDirtyMember ? onlyDirtyMember.resetButtonText : resetButtonText}
        </Button>

        {dirtyMembers.map((member) => (
          <Button
            key={member.id}
            type="button"
            onPointerDownCapture={flushActiveField}
            onClick={member.onSave}
            disabled={anySavePending}
            className="w-auto"
          >
            {member.isSavePending ? <LoadingSpinner className="size-4" /> : null}
            {member.submitButtonText}
          </Button>
        ))}
      </div>
    </FixedSaveBarFrame>
  ) : null

  const shouldUsePortal = renderInPortal && !isInSheet

  return (
    <SaveBarGroupContext.Provider value={contextValue}>
      {children}
      {groupedSaveBar && shouldUsePortal
        ? createPortal(groupedSaveBar, document.body)
        : groupedSaveBar}
    </SaveBarGroupContext.Provider>
  )
}

const SaveBar = ({
  onReset = null,
  mutation,
  onSave = null,
  submitButtonText = 'Save',
  resetButtonText = 'Reset',
  unsavedChangesText = 'Unsaved changes',
  renderInPortal = true,
  forceDirty = false,
  layout = 'fixed',
  shouldBlockNavigation = null,
  isInteractionPending = false,
  onDirtyChange = null,
}: SaveBarProps) => {
  const form = useFormContext()
  const isInSheet = useSheetContext()
  const group = useContext(SaveBarGroupContext)
  const groupMemberId = useId()

  // Ok, hear me out...
  // TLDR; this makes it so that the user won't have to click `Reset` twice sometimes.
  // Our SelectInput has a list of options that could change and no longer contain the default value.
  // If you click reset in that state, it will update the state before the options have had time to update.
  // So, you'll end up with an empty-looking SelectInput. Doing a reset, and then scheduling another one
  // after the next render ensures that the `options` in any SelectInputs will have had time to update.
  const resetForm = useCallback(() => {
    form.reset()
    setTimeout(() => form.reset())
  }, [form])
  const resetHandler = onReset ?? resetForm

  // Have any of the values changed since the form was initialized
  const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const hasUnsavedChanges = forceDirty || !isDefaultValue
  const isSavePending = isSubmitting || mutation?.isPending || isInteractionPending
  const isResetPending = isSubmitting || isInteractionPending
  // Let non-navigation controls protect edits before they replace the form subtree.
  const isGroupedFixedSaveBar = Boolean(group && layout === 'fixed')

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges)

    return () => onDirtyChange?.(false)
  }, [hasUnsavedChanges, onDirtyChange])

  const handleReset = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      flushActiveField()
      resetHandler(event)
    },
    [resetHandler]
  )
  const handleSave = useCallback(() => {
    flushActiveField()
    return onSave ? onSave() : form.handleSubmit()
  }, [form, onSave])

  useEffect(() => {
    if (!group || layout !== 'fixed') return

    return group.register(groupMemberId)
  }, [group, groupMemberId, layout])

  useEffect(() => {
    if (!group || layout !== 'fixed') return

    group.update(groupMemberId, {
      id: groupMemberId,
      isDirty: hasUnsavedChanges,
      isDefaultValue,
      isResetPending,
      isSavePending,
      onReset: handleReset,
      onSave: handleSave,
      resetButtonText,
      shouldBlockNavigation,
      submitButtonText,
      unsavedChangesText,
    })
  }, [
    group,
    groupMemberId,
    handleReset,
    handleSave,
    hasUnsavedChanges,
    isDefaultValue,
    isResetPending,
    isSavePending,
    layout,
    resetButtonText,
    shouldBlockNavigation,
    submitButtonText,
    unsavedChangesText,
  ])

  useUnsavedChangesBlocker({
    disabled: isGroupedFixedSaveBar,
    hasUnsavedChanges,
    shouldBlockNavigation:
      typeof shouldBlockNavigation === 'function'
        ? ({ next }) => shouldBlockNavigation({ next, isDefaultValue, hasUnsavedChanges })
        : null,
  })

  if (!hasUnsavedChanges) return null

  if (isGroupedFixedSaveBar) return null

  const resetButton = (
    <Button
      type="button"
      variant="link"
      onPointerDownCapture={flushActiveField}
      onClick={handleReset}
      disabled={isResetPending}
    >
      {resetButtonText}
    </Button>
  )
  const saveButton = (
    <Button
      type="button"
      onPointerDownCapture={flushActiveField}
      onClick={handleSave}
      disabled={isSavePending}
      className="w-auto"
    >
      {isSavePending ? <LoadingSpinner className="size-4" /> : null}
      {submitButtonText}
    </Button>
  )

  if (layout === 'inline') {
    return (
      <div className="flex shrink-0 items-center justify-end gap-2">
        {resetButton}
        {saveButton}
      </div>
    )
  }

  const saveBarContent = (
    <FixedSaveBarFrame unsavedChangesText={unsavedChangesText}>
      <div className="flex items-center gap-2 sm:gap-4">
        {resetButton}
        {/* Because we're passing through a portal (maintains consistent positioning throughout the app),
            the form.SubmitButton doesn't work because it's not inside the form's html context. Calling
            form.handleSubmit() will trigger the form to submit. This is more explicit than using the
            form.SubmitButton and relying on positioning inside the form. */}
        {saveButton}
      </div>
    </FixedSaveBarFrame>
  )

  // Use portal to render at the top of the document body, unless explicitly disabled or in sheet context
  const shouldUsePortal = renderInPortal && !isInSheet
  return shouldUsePortal ? createPortal(saveBarContent, document.body) : saveBarContent
}

export default SaveBar
